import { useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { apiClient } from '@/lib/apiClient';
import { USE_MOCKS } from '@/lib/mockConfig';
import type { ApiResponse } from '@/types/api';
import {
  initialUserProfile,
  initialBusinessProfile,
  type UserProfile,
  type BusinessProfile,
  type BusinessType,
} from '../mockSettings';

const fallbackUser: UserProfile = {
  fullName: '',
  email: '',
  avatarInitials: 'U',
  avatarUrl: null,
};
const fallbackBusiness: BusinessProfile = {
  businessName: '',
  businessType: 'other',
  description: '',
  logoInitials: '',
  logoUrl: null,
  timezone: 'Asia/Dhaka',
  currency: 'BDT',
  supportEmail: '',
};

const initials = (name: string) =>
  name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || 'U';

/**
 * Loads/persists the backable Settings sections:
 *  - Profile   → GET/PATCH /api/auth/me (real: the logged-in user's name/email)
 *  - Business  → GET/PATCH /api/chatbot/config (name/type/description; the extra
 *                fields — timezone/currency/support email — ride in settingsJson.business)
 * Team, billing, notifications, developer, danger-zone stay local until they
 * have a backend. Falls back to mock defaults when unauthenticated / empty DB.
 */
export function useSettings() {
  const rawSettingsRef = useRef<Record<string, any>>({});

  const profileQuery = useQuery({
    queryKey: ['settings', 'profile'],
    queryFn: () =>
      apiClient.get<ApiResponse<any>>('/api/auth/me')
        .then((res) => {
          const u = res.data.data;
          if (!u) return fallbackUser;
          const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
          return { fullName, email: u.email, avatarInitials: initials(fullName), avatarUrl: null } as UserProfile;
        })
        .catch(() => fallbackUser),
    staleTime: 5 * 60 * 1000,
  });

  const businessQuery = useQuery({
    queryKey: ['settings', 'business'],
    queryFn: () =>
      apiClient.get<ApiResponse<any>>('/api/chatbot/config')
        .then((res) => {
          const cfg = res.data.data;
          if (!cfg) return fallbackBusiness;
          rawSettingsRef.current = cfg.settingsJson || {};
          const biz = (cfg.settingsJson?.business || {}) as Partial<BusinessProfile>;
          const businessName = cfg.businessName || fallbackBusiness.businessName;
          return {
            ...fallbackBusiness,
            businessName,
            businessType: (biz.businessType || cfg.businessType || fallbackBusiness.businessType) as BusinessType,
            description: cfg.description ?? fallbackBusiness.description,
            timezone: biz.timezone || fallbackBusiness.timezone,
            currency: biz.currency || fallbackBusiness.currency,
            supportEmail: biz.supportEmail || fallbackBusiness.supportEmail,
            logoInitials: initials(businessName),
            logoUrl: null,
          } as BusinessProfile;
        })
        .catch(() => fallbackBusiness),
    staleTime: 5 * 60 * 1000,
  });

  const saveProfile = useMutation({
    mutationFn: (patch: Partial<UserProfile>) => {
      const body: Record<string, string> = {};
      if (patch.fullName !== undefined) {
        const parts = patch.fullName.trim().split(/\s+/);
        body.firstName = parts[0] || '';
        body.lastName = parts.slice(1).join(' ');
      }
      return apiClient.patch('/api/auth/me', body);
    },
    onError: () => {},
  });

  const saveBusiness = useMutation({
    mutationFn: (patch: Partial<BusinessProfile>) => {
      // The business block below is rebuilt from what was loaded. After a failed load that is
      // nothing, so saving would drop the fields not on screen. Refuse, and say so.
      if (businessQuery.isError) {
        toast.error("Your business settings didn't load, so nothing was saved. Refresh and try again.");
        return Promise.resolve(null);
      }
      const business = {
        ...(rawSettingsRef.current.business || {}),
        ...(patch.businessType !== undefined && { businessType: patch.businessType }),
        ...(patch.timezone !== undefined && { timezone: patch.timezone }),
        ...(patch.currency !== undefined && { currency: patch.currency }),
        ...(patch.supportEmail !== undefined && { supportEmail: patch.supportEmail }),
      };
      const body: Record<string, any> = { settingsJson: { ...rawSettingsRef.current, business } };
      if (patch.description !== undefined) body.description = patch.description;
      return apiClient.patch('/api/chatbot/config', body);
    },
    onError: () => {},
  });

  return {
    userProfile: profileQuery.data ?? fallbackUser,
    businessProfile: businessQuery.data ?? fallbackBusiness,
    isLoading: profileQuery.isLoading || businessQuery.isLoading,
    saveProfile: saveProfile.mutate,
    saveBusiness: saveBusiness.mutate,
  };
}
